import type { ClockSync } from "@/network/ClockSync";

const SEEK_THRESHOLD_SECONDS = 0.75;
const RATE_ADJUST_THRESHOLD_SECONDS = 0.08;
const CORRECTION_RATE_DELTA = 0.05;
const CORRECTION_INTERVAL_MS = 500;

export interface VideoSyncDebugInfo {
  expected: number;
  actual: number;
  syncErrorMs: number;
}

/**
 * Keeps the ceremony video aligned to the server-scheduled start time.
 * Prioritizes smooth playback over frame-perfect sync: small drift is
 * absorbed with a temporary playbackRate nudge, large drift triggers a seek.
 */
export class VideoSyncController {
  private ceremonyStartAt: number | null = null;
  private intervalHandle: number | null = null;
  private lastDebug: VideoSyncDebugInfo = { expected: 0, actual: 0, syncErrorMs: 0 };

  constructor(private readonly video: HTMLVideoElement, private readonly clockSync: ClockSync) {}

  /** Starts (or jumps into, for reconnects/late joins) playback synced to ceremonyStartAt. */
  async startAt(ceremonyStartAt: number): Promise<boolean> {
    this.ceremonyStartAt = ceremonyStartAt;
    const expected = Math.max(0, (this.clockSync.serverNow() - ceremonyStartAt) / 1000);
    try {
      this.video.currentTime = expected;
    } catch {
      /* currentTime may not be settable until metadata loads; correction loop will catch up */
    }
    let played = true;
    try {
      await this.video.play();
    } catch {
      played = false;
    }
    this.beginCorrectionLoop();
    return played;
  }

  private beginCorrectionLoop(): void {
    if (this.intervalHandle !== null) return;
    this.intervalHandle = window.setInterval(() => this.correct(), CORRECTION_INTERVAL_MS);
  }

  private correct(): void {
    if (this.ceremonyStartAt === null || this.video.paused || this.video.ended) return;
    const expected = Math.max(0, (this.clockSync.serverNow() - this.ceremonyStartAt) / 1000);
    const diff = expected - this.video.currentTime;
    this.lastDebug = { expected, actual: this.video.currentTime, syncErrorMs: Math.round(diff * 1000) };

    if (Math.abs(diff) > SEEK_THRESHOLD_SECONDS) {
      this.video.currentTime = expected;
      this.video.playbackRate = 1;
    } else if (Math.abs(diff) > RATE_ADJUST_THRESHOLD_SECONDS) {
      this.video.playbackRate = 1 + Math.sign(diff) * CORRECTION_RATE_DELTA;
    } else {
      this.video.playbackRate = 1;
    }
  }

  getDebugInfo(): VideoSyncDebugInfo {
    return this.lastDebug;
  }

  /** Manual retry for a play() call blocked by autoplay policy (e.g. bind to a tap-to-continue fallback). */
  async retryPlay(): Promise<boolean> {
    try {
      await this.video.play();
      return true;
    } catch {
      return false;
    }
  }

  stop(): void {
    if (this.intervalHandle !== null) {
      window.clearInterval(this.intervalHandle);
      this.intervalHandle = null;
    }
    this.video.playbackRate = 1;
    this.ceremonyStartAt = null;
  }
}
